import { HttpClient, HttpHeaders, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { DriverDTO } from "@shared/models/driver-dto";
import { environment } from "@env";
import { SaveVehicleDTO } from "@shared/models/save-vehicle-dto";
import { SearchFormDTO } from "../../modules/private/modules/vehicle/components/models/search-form-dto";
import { VehicleDTO, VehicleTableViewDTO } from "@shared/models/vehicle-dto";
import { map } from "rxjs/operators";
import { LanguageService } from "./language.service";
import {
  VehicleMileageHistoryDTO,
  VehicleUpdateStatusDTO,
  VehicleStatusHistoryDTO,
  VehicleStatusResponseDTO
} from "@core/dtos";
import { PageManager } from "@shared/models/page-manager";
import { Pagination } from "@shared/models/pagination";
import { ExpenseGroupDTO } from "@shared/models/expense-group-dto";
import { NearbyHistoryCarWorkshopDTO } from "@shared/models/nearby-history-car-workshop-dto";
import {
  ServiceRequestMaintenanceHistory,
  ServicesRequestByVehicle
} from "@shared/models/service-request-vehicle-dto";
import { BudgetStatusVehicle } from "@shared/enums/budget-status-vehicle.enum";
import { VehicleMaintenanceHistoryDTO } from "@shared/models/vehicle-maintenance-history-dto";
import { DateHelperService } from "./date.helper.service";
import { VehicleDriverProfileDTO } from "@shared/models/vehicle-driver-details-dto";
import { removeAllEmptyValues } from "@shared/helper";
import { VehicleProfileMileageTableSearchForm } from "@core/models";
import { VehicleProfileAssociationHistoryDTO } from "@core/dtos";
import { Sort } from "@angular/material/sort";
import { DISABLED_NOTIFY_MESSAGE } from "@core/constants";
import { VehicleMaintenanceHistoryToReportDTO } from "@shared/models/vehicle-maintenance-history-report";

@Injectable({
  providedIn: "root"
})
export class VehicleService {
  private vehiclesUrl = "vehicles/";
  private baseUrl = `${environment.apiUrl}${this.vehiclesUrl}`;
  private headers = new HttpHeaders({
    [DISABLED_NOTIFY_MESSAGE]: "true"
  });

  constructor(
    private http: HttpClient,
    private languageService: LanguageService,
    private dateHelperService: DateHelperService
  ) {}

  listExpenseGroups(): Observable<ExpenseGroupDTO[]> {
    return this.http.get<ExpenseGroupDTO[]>(
      environment.apiUrl + `${this.vehiclesUrl}expense-group`
    );
  }

  findDriversByVehicleId(vehicleId: number | string): Observable<DriverDTO[]> {
    return this.http.get<DriverDTO[]>(
      environment.apiUrl + `${this.vehiclesUrl}${vehicleId}/drivers`
    );
  }

  findDriversProfileByVehicleId(
    vehicleId: number | string
  ): Observable<VehicleDriverProfileDTO[]> {
    return this.http.get<VehicleDriverProfileDTO[]>(
      environment.apiUrl + `${this.vehiclesUrl}${vehicleId}/drivers/profile`
    );
  }

  getVehicleAssociationHistory(
    vehicleId: number | string
  ): Observable<VehicleProfileAssociationHistoryDTO[]> {
    return this.http.get<VehicleProfileAssociationHistoryDTO[]>(
      `${environment.apiUrl}${this.vehiclesUrl}${vehicleId}/drivers/history`
    );
  }

  saveDriver(vehicleId: number, driverId): Observable<any> {
    return this.http.post<any>(
      environment.apiUrl +
        `${this.vehiclesUrl}${vehicleId}/drivers/${driverId}`,
      null
    );
  }

  deleteDriver(vehicleId: number, driverId): Observable<any> {
    return this.http.delete<any>(
      environment.apiUrl + `${this.vehiclesUrl}${vehicleId}/drivers/${driverId}`
    );
  }

  save(saveVehicleDTO: SaveVehicleDTO): Observable<any> {
    return this.http.post<any>(
      environment.apiUrl + this.vehiclesUrl,
      saveVehicleDTO
    );
  }

  delete(id: number): Observable<any> {
    return this.http.delete<any>(
      environment.apiUrl + `${this.vehiclesUrl}${id}`
    );
  }

  findById(id: number): Observable<SaveVehicleDTO> {
    return this.http.get<SaveVehicleDTO>(
      environment.apiUrl + `${this.vehiclesUrl}${id}`
    );
  }

  findDriver(id: number): Observable<DriverDTO[]> {
    return this.http.get<DriverDTO[]>(
      environment.apiUrl + `${this.vehiclesUrl}${id}/drivers`
    );
  }

  findVehicleMaintenanceHistoryReportData(vehicleId: number) {
    return this.http.get<VehicleMaintenanceHistoryToReportDTO>(
      environment.apiUrl +
        `${this.vehiclesUrl}${vehicleId}/maintenance-history/report`
    );
  }

  findByFilters(
    searchFormDTO: SearchFormDTO,
    pageManager: PageManager,
    sort: string
  ): Observable<Pagination<VehicleTableViewDTO[]>> {
    let query = "";
    if (searchFormDTO.licensePlate) {
      query += `&licensePlate=${searchFormDTO.licensePlate}`;
    }
    if (searchFormDTO.brandId) {
      query += `&brandId=${searchFormDTO.brandId}`;
    }
    if (searchFormDTO.clientId) {
      query += `&clientId=${searchFormDTO.clientId}`;
    }
    if (searchFormDTO.modelId) {
      query += `&modelId=${searchFormDTO.modelId}`;
    }
    if (searchFormDTO.modelYear) {
      query += `&modelYear=${searchFormDTO.modelYear}`;
    }
    if (searchFormDTO.blockId) {
      query += `&blockId=${searchFormDTO.blockId}`;
    }
    if (searchFormDTO.driverId) {
      query += `&driverId=${searchFormDTO.driverId}`;
    }

    if (searchFormDTO.active === true || searchFormDTO.active === false) {
      query += `&active=${searchFormDTO.active}`;
    }

    if (
      searchFormDTO.haveMaintenancePlan === true ||
      searchFormDTO.haveMaintenancePlan === false
    ) {
      query += `&haveMaintenancePlan=${searchFormDTO.haveMaintenancePlan}`;
    }

    if (
      searchFormDTO.havePriceTable === true ||
      searchFormDTO.havePriceTable === false
    ) {
      query += `&havePriceTable=${searchFormDTO.havePriceTable}`;
    }

    return this.http.get<Pagination<VehicleTableViewDTO[]>>(
      environment.apiUrl +
        `${this.vehiclesUrl}filters?page=${pageManager.pageIndex}&size=${pageManager.pageSize}${query}${sort}`
    );
  }

  edit(id: number, saveVehicleDTO: SaveVehicleDTO): Observable<any> {
    return this.http.put<any>(
      environment.apiUrl + `${this.vehiclesUrl}${id}`,
      saveVehicleDTO
    );
  }

  updateKm(id: number, currentKm: number): Observable<SaveVehicleDTO> {
    const query = `?currentKm=${currentKm}`;
    return this.http.put<any>(
      `${environment.apiUrl}${this.vehiclesUrl}${id}/update-km${query}`,
      {}
    );
  }

  inProgress(vehicleId: number): Observable<boolean> {
    return this.http.get<boolean>(
      `${environment.apiUrl}${this.vehiclesUrl}${vehicleId}/service-requests/in-progress`
    );
  }

  getCarworkshopsByHistory(
    vehicleId: number,
    filter?: any,
    maintenancePlanPackageId: number | null = null
  ): Observable<NearbyHistoryCarWorkshopDTO[]> {
    let params = new HttpParams();

    if (filter) {
      Object.keys(filter).forEach((key) => {
        const value = filter[key];
        if (value === undefined || value === "") {
          return;
        }

        params = params.set(
          key,
          Array.isArray(value) ? value.join(",") : `${value}`
        );
      });
    }

    if (maintenancePlanPackageId) {
      params = params.set(
        "maintenancePlanPackageId",
        `${maintenancePlanPackageId}`
      );
    }

    return this.http.get<NearbyHistoryCarWorkshopDTO[]>(
      `${environment.apiUrl}${this.vehiclesUrl}${vehicleId}/car-workshops/history`,
      { params, headers: this.headers }
    );
  }

  getVehicleProfile(vehicleId: number): Observable<any> {
    return this.http.get(
      `${environment.apiUrl}${this.vehiclesUrl}${vehicleId}/profile`,
      {
        headers: this.headers
      }
    );
  }

  getVehicleMaintenance(vehicleId: number): Observable<any> {
    return this.http.get(
      `${environment.apiUrl}${this.vehiclesUrl}${vehicleId}/maintenance-info`
    );
  }

  getAllVehicleBudgetStatus() {
    return this.http.get<{ name: string; value: BudgetStatusVehicle }[]>(
      `${environment.apiUrl}${this.vehiclesUrl}budget-status`
    );
  }

  getVehicleMaintenanceStatus(vehicleId: number): Observable<any> {
    return this.http.get(
      `${environment.apiUrl}${this.vehiclesUrl}${vehicleId}/maintenance-status`
    );
  }

  getServicesRequestsByVehicle(
    vehicleId: number,
    filter: {
      budgetStatus?: string;
      carWorkshop?: string;
      requestId?: string;
      serviceType?: string;
      status?: string;
    },
    pageManager: PageManager,
    sort: string
  ) {
    const params = {
      sort: sort.split("&sort="),
      page: pageManager.pageIndex.toString(),
      size: pageManager.pageSize.toString()
    };

    // Remove empty keys
    for (const key in filter) !filter[key] ? delete filter[key] : null;

    if (filter) Object.assign(params, filter);

    return this.http.get<Pagination<ServicesRequestByVehicle[]>>(
      `${environment.apiUrl}${this.vehiclesUrl}${vehicleId}/service-requests`,
      { params }
    );
  }

  getMaintenanceHistory(
    vehicleId: number,
    filter: {
      initialDate?: Date;
      finalDate?: Date;
      requestId?: string;
      budgetItemName?: string;
      serviceType?: string;
    },
    pageManager: PageManager
  ) {
    let query = "";
    if (filter.initialDate)
      query += `&initialDate=${this.dateHelperService.convertToDefaultFormat(
        filter.initialDate
      )}`;
    if (filter.finalDate)
      query += `&finalDate=${this.dateHelperService.convertToDefaultFormat(
        filter.finalDate
      )}`;
    if (filter.requestId) query += `&requestId=${filter.requestId}`;
    if (filter.serviceType) query += `&serviceType=${filter.serviceType}`;
    if (filter.budgetItemName)
      query += `&budgetItemName=${filter.budgetItemName}`;

    query += `&page=${pageManager.pageIndex.toString()}`;
    query += `&size=${pageManager.pageSize.toString()}`;

    return this.http.get<Pagination<VehicleMaintenanceHistoryDTO[]>>(
      `${environment.apiUrl}${this.vehiclesUrl}${vehicleId}/maintenance-history?${query}`
    );
  }

  getServiceRequestMaintenanceHistory(vehicleId: number, requestId: number) {
    return this.http.get<ServiceRequestMaintenanceHistory[]>(
      `${environment.apiUrl}${this.vehiclesUrl}${vehicleId}/maintenance-history/${requestId}`
    );
  }

  getMileageHistory(
    vehicleId: number,
    filterParams?: { initialDate: string }
  ): Observable<VehicleMileageHistoryDTO[]> {
    const params = new HttpParams({
      fromObject: { ...removeAllEmptyValues(filterParams) }
    });
    return this.http.get<VehicleMileageHistoryDTO[]>(
      `${environment.apiUrl}${this.vehiclesUrl}${vehicleId}/mileage-history`,
      { params }
    );
  }

  getMileageHistoryPaginated(
    vehicleId: number,
    filterParams?: VehicleProfileMileageTableSearchForm
  ): Observable<Pagination<VehicleMileageHistoryDTO[]>> {
    let params = new HttpParams({
      fromObject: {
        pagination: "true"
      }
    });

    if (filterParams) {
      const pageManager = createPage(filterParams.pageManager);
      const sort = createSort(filterParams.sort);

      params = new HttpParams({
        fromObject: {
          ...removeAllEmptyValues({
            ...pageManager,
            ...sort,
            pagination: true
          })
        }
      });
    }

    return this.http.get<Pagination<VehicleMileageHistoryDTO[]>>(
      `${this.baseUrl}${vehicleId}/mileage-history`,
      { params }
    );
  }

  updateVehicleStatus(
    vehicleId: number,
    active: boolean
  ): Observable<VehicleStatusResponseDTO> {
    return this.http.patch<VehicleStatusResponseDTO>(
      `${environment.apiUrl}v2/vehicles/update-status/${vehicleId}`,
      { active }
    );
  }

  getVehicleStatusHistory(
    vehicleId: number
  ): Observable<VehicleStatusHistoryDTO[]> {
    return this.http.get<VehicleStatusHistoryDTO[]>(
      `${environment.apiUrl}v2/vehicles/vehicle-status-history/${vehicleId}`
    );
  }
}

function createPage(pageManager: PageManager) {
  if (!pageManager) return;
  return { page: pageManager.pageIndex, size: pageManager.pageSize };
}

function createSort(sort: Sort) {
  if (!sort) return;

  if (!sort.active)
    return {
      sort: `id,asc`
    };

  return {
    sort: `${sort.active},${sort.direction}`
  };
}
